'use client';
import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { Toast, ToastType } from './Toast';
import styles from './ToastContainer.module.css';

interface ToastData {
	id: string;
	message: string;
	type: ToastType;
}

interface ToastContextType {
	showToast: (message: string, type: ToastType) => void;
	success: (message: string) => void;
	error: (message: string) => void;
	info: (message: string) => void;
	dismiss: (type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
	const [toasts, setToasts] = useState<ToastData[]>([]);

	const showToast = useCallback((message: string, type: ToastType) => {
		const id = Math.random().toString(36).substring(2, 9);
		setToasts((prev) => [...prev, { id, message, type }]);
	}, []);

	const removeToast = useCallback((id: string) => {
		setToasts((prev) => prev.filter((toast) => toast.id !== id));
	}, []);

	const success = useCallback((message: string) => showToast(message, 'success'), [showToast]);
	const error = useCallback((message: string) => showToast(message, 'error'), [showToast]);
	const info = useCallback((message: string) => showToast(message, 'info'), [showToast]);
	const dismiss = useCallback((type?: ToastType) => {
		setToasts((prev) => (type ? prev.filter((toast) => toast.type !== type) : []));
	}, []);

	return (
		<ToastContext.Provider value={{ showToast, success, error, info, dismiss }}>
			{children}
			<div className={styles.container}>
				{toasts.map((toast) => (
					<Toast
						key={toast.id}
						message={toast.message}
						type={toast.type}
						onClose={() => removeToast(toast.id)}
					/>
				))}
			</div>
		</ToastContext.Provider>
	);
};

export const useToast = () => {
	const context = useContext(ToastContext);
	if (!context) {
		throw new Error('useToast must be used within ToastProvider');
	}
	return context;
};
